import React from "react";
import {motion} from "framer-motion";
import {Lightbulb} from "lucide-react";
import {useTranslation} from "react-i18next";

/**
 * Shared shell for every analytics chart: title bar, loading skeleton and a
 * consistent empty state. Pass `isEmpty` to render the empty placeholder.
 */
const ChartCard = ({
    title,
    icon: Icon,
    isLoading,
    isEmpty,
    isDarkMode,
    minHeight = 280,
    children,
}) => {
    const {t} = useTranslation("activite");
    return (
        <motion.div
            layout
            initial={{opacity: 0, y: 12}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -12}}
            transition={{duration: 0.25}}
            className={`flex h-full flex-col overflow-hidden rounded-xl border shadow-sm ${
                isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
            }`}
        >
            <div
                className={`flex items-center gap-2 border-b px-4 py-3 ${
                    isDarkMode ? "border-gray-700" : "border-gray-100"
                }`}
            >
                {Icon && <Icon className="h-5 w-5 text-teal-500" strokeWidth={2} />}
                <h2
                    className={`text-sm font-semibold ${
                        isDarkMode ? "text-gray-100" : "text-gray-800"
                    }`}
                >
                    {title}
                </h2>
            </div>

            <div className="flex-1 p-4">
                {isLoading ? (
                    <div
                        className="flex flex-col items-center justify-center"
                        style={{minHeight}}
                    >
                        <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-200 border-t-teal-500" />
                        <p
                            className={`mt-3 text-sm ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                        >
                            {t("dashboard.loading")}
                        </p>
                    </div>
                ) : isEmpty ? (
                    <div
                        className={`flex flex-col items-center justify-center rounded-lg text-center ${
                            isDarkMode ? "bg-gray-700/40" : "bg-gray-50"
                        }`}
                        style={{minHeight}}
                    >
                        <div className="mb-3 rounded-full bg-yellow-100 p-3">
                            <Lightbulb size={32} className="text-yellow-500" strokeWidth={1.5} />
                        </div>
                        <h3
                            className={`mb-1 text-sm font-medium ${
                                isDarkMode ? "text-gray-100" : "text-gray-800"
                            }`}
                        >
                            {t("dashboard.noDataTitle")}
                        </h3>
                        <p
                            className={`max-w-xs px-4 text-xs ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                        >
                            {t("dashboard.noDataBody")}
                        </p>
                    </div>
                ) : (
                    children
                )}
            </div>
        </motion.div>
    );
};

export default ChartCard;
